import React, { ReactElement, useState } from 'react';

import {
  Divider,
} from '@volkswagen-onehub/components-core';
import { Breakpoints, TextColor, TextCustom, TextLineHeight, TextSize, TextWeight, useIsMobile } from '@vw-marketing/us-components';
import { FeatureAppLoader } from '@feature-hub/react';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { applyTextTransformations } from '../../../shared/utils/applyTextTransformations';
import { Arrow } from '../../../shared/components/arrow';
import { DeactivateAccountConfirmationView } from '../components/deactivate-account-confirmation/DeactivateAccountConfirmationView';
import { DeactivateAccountCTA } from '../components/deactivate-account-cta';
import { DeactivateAccountErrorView } from '../components/deactivate-account-error/DeactivateAccountErrorView';
import { DeactivateAccountLoadingView } from '../components/deactivate-account-confirmation/DeactivateAccountLoadingView';
import { DeactivateAccountView } from '../components/deactivate-account/DeactivateAccountView';
import { EditFullNameFormView } from '../components/edit-full-name-form/EditFullNameFormView';
import { EditHomeAddressFormView } from '../components/edit-home-address-from/EditHomeAddressFormView';
import { EditPhoneFormView } from '../components/edit-phone-form/EditPhoneFormView';
import { EditSecurityPinView } from '../components/edit-security-pin/EditSecurityPinView';
import { EditSecurityQuestionsFormView } from '../components/edit-security-questions/EditSecurityQuestionsFormView';
import { ManageContactsView } from '../components/manage-contacts/ManageContactsView';
import {
  formatPhoneNumber,
  getFormattedAddress,
  getFullName,
  getPrimaryPhone,
} from '../../../shared/utils/general';
import { useFeatureAppConfig } from '../../../shared/hooks/use-feature-app-config';
// import { useFeatureAppEnvironment } from '../../../shared/hooks/use-feature-app-environment';
import { useAuth } from '../../../app/providers/AuthContext';
import { useFeatureServices } from '../../../shared/hooks/use-feature-services';
import { useMyVwContext } from '../../../shared/hooks/use-my-vw-context';
import { UserData } from '../../../shared/typings/main';
import { useUser } from '../../../app/providers/UserContext';
import { useVehicle } from '../../../app/providers/VehicleContext';
import { useTrackingManager } from '../../../shared/hooks/use-tracking-manager';
import {
  onVWBasicLinkClickWithProcessEvent,
  onVWBasicStepLoadEvent
} from '../../../shared/tagging/tagging-helpers';
import ListItem from '../components/list-item';
import { useToast } from '../../../shared/context/ToastContext';

enum EditActionEnum {
  'FULL_NAME',
  'HOME_ADDRESS',
  'MOBILE_NUMBER',
  'SECURITY_QUESTIONS',
  'EMAIL_ADDRESS',
  'PIN',
  'MANAGE_CONTACTS'
}

export const useAccountSettings = () => {
  const [active, setActive] = useState<boolean>(false);
  const [changes, setChanges] = useState<boolean>(false);
  const [content, setContent] = useState<ReactElement | undefined>();
  const [goBack, setGoBack] = useState<(() => void) | undefined>(undefined);
  const [editEmailModal, setEditEmailModal] = useState<boolean>(false);
  const [isDeactivating, setIsDeactivating] = useState<boolean>(false);
  const [noTranslateTerms, setNoTranslateTerms] = useState<string[]>([]);
  const [title, setTitle] = useState<React.ReactNode>('');

  const { authService, translationHandler, baseUrl: urlMyVW } = useMyVwContext();
  const trackingManager = useTrackingManager();
  const { showToast } = useToast();

  const { myVwFaRolloutAccountSettingsSecurity } = useFlags();

  const { 'featureapp-service': featureAppService, 'locale-service': localeService } = useFeatureServices();
  const { accountSettingsFAName = '' } = useFeatureAppConfig();

  const isDesktop: boolean = !useIsMobile(Breakpoints.b1920);
  const { logout } = useAuth();
  const localeCode = `${localeService.language}-${localeService.countryCode}`;
  const isCanada = localeService.countryCode === 'CA';
  const isFrench = localeService.language === 'fr';

  const {
    fetchUserSecurityQuestions,
    securityQuestions,
    updateUserProfile,
    user,
    userSecurityQuestions,
    fetchSaveAdditionalContacts
  } = useUser();
  const { fetchAdditionalVehiclesInformation, isLoadingVehicleData } = useVehicle();

  const faConfiguration =
    featureAppService?.featureAppConfigurations[accountSettingsFAName];
  const userData = (user || ({} as any)) as UserData;
  const initialPhoneNumber: string = getPrimaryPhone(userData);
  const formattedNumber: string =
    formatPhoneNumber(initialPhoneNumber).formatted;

  const {
    baseUrl,
    config = {},
    src,
    serverSrc,
    name: featureAppName,
  } = (faConfiguration || {}) as any;

  // For local run uncomment these lines
  // const { config } = useFeatureAppEnvironment();
  // const baseUrl = 'https://qa.vwmyvwaccountsettingsfeatureapp.svc.vwusa.io';
  // const src = 'featureApp.js';

  // Helper function to track link clicks
  const trackLinkClick = (linkName: string) => {
    try {
      onVWBasicLinkClickWithProcessEvent(trackingManager, {
        Name: 'Account Settings',
        StepName: 'Personal Information and Security',
        StepNumber: '4',
      }, {
        name: linkName,
      });
    } catch (error) {
      console.warn('Error tracking link click:', error);
    }
  };

  // Helper function to track step load events
  const trackStepLoad = (stepName: string, stepNumber: string) => {
    try {
      onVWBasicStepLoadEvent(trackingManager, {
        Name: 'Account Settings',
        StepName: stepName,
        StepNumber: stepNumber,
      });
    } catch (error) {
      console.warn('Error tracking step load:', error);
    }
  };

  const handleEditAction = (value: EditActionEnum) => {
    switch (value) {
      case EditActionEnum.FULL_NAME:
        // Track step load for Full Name editing
        trackStepLoad('Personal Information and Security - Full Name', '4.1');

        setTitle(translationHandler('accountSettings.edit.fullName.title'));
        setContent(
          <EditFullNameFormView
            authService={authService}
            translationHandler={translationHandler}
            trackingManager={trackingManager}
            localeCode={localeCode}
            updateUserProfile={updateUserProfile}
            initialFirstName={userData.firstName || ''}
            initialLastName={userData.lastName || ''}
            onSuccess={() => {
              setActive(false);
              setChanges(false);
              showToast(translationHandler('accountSettings.edit.success'), 'Success');
            }}
            onChange={() => {
              setChanges(true);
            }}
            isCanada={isCanada}
          />,
        );
        setActive(true);
        setChanges(false);
        break;
      case EditActionEnum.HOME_ADDRESS:
        // Track step load for Home Address editing
        trackStepLoad('Personal Information and Security - Home Address', '4.2');

        setTitle(translationHandler('accountSettings.edit.homeAddress.title'));
        setContent(
          <EditHomeAddressFormView
            authService={authService}
            translationHandler={translationHandler}
            trackingManager={trackingManager}
            localeCode={localeCode}
            isCanada={isCanada}
            isFrench={isFrench}
            updateUserProfile={updateUserProfile}
            user={userData}
            onSuccess={() => {
              setActive(false);
              setChanges(false);
              showToast(translationHandler('accountSettings.edit.success'), 'Success');
            }}
            onChange={() => {
              setChanges(true);
            }}
            baseUrl={urlMyVW}
          />,
        );
        setActive(true);
        setChanges(false);
        break;
      case EditActionEnum.MOBILE_NUMBER:
        // Track step load for Mobile Number editing
        trackStepLoad('Personal Information and Security - Mobile Number', '4.3');

        setTitle(translationHandler('accountSettings.edit.mobileNumber.title'));
        setContent(
          <EditPhoneFormView
            authService={authService}
            translationHandler={translationHandler}
            trackingManager={trackingManager}
            localeCode={localeCode}
            updateUserProfile={updateUserProfile}
            user={userData}
            onSuccess={() => {
              setActive(false);
              setChanges(false);
              showToast(translationHandler('accountSettings.edit.success'), 'Success');
            }}
            onChange={() => {
              setChanges(true);
            }}
            isCanada={isCanada}
          />,
        );
        setActive(true);
        setChanges(false);
        break;
      case EditActionEnum.MANAGE_CONTACTS:
        trackStepLoad('Personal Information and Security - Manage Contacts', '4.5');

        setGoBack(undefined);
        setTitle(
          <TextCustom
            style={{
              [Breakpoints.default]: {
                fontSize: TextSize.TextSize20,
                fontWeight: TextWeight.bold,
                color: TextColor.primary,
                lineHeight: TextLineHeight.TextLineHeight24,
              },
              [Breakpoints.b1280]: {
                fontSize: TextSize.TextSize24,
                lineHeight: TextLineHeight.TextLineHeight28,
              },
            }}
          >
            {translationHandler('accountSettings.manage.contacts.title')}
          </TextCustom>
        );
        setContent(
          <ManageContactsView
            trackingManager={trackingManager}
            translationHandler={translationHandler}
            setTitle={setTitle}
            setContent={setContent}
            setGoBack={setGoBack}
            onClose={() => { setActive(false); setChanges(false); setGoBack(undefined); }}
            onChange={() => { setChanges(true); }}
            onResetChanges={() => { setChanges(false); }}
            onSaveSuccess={(message) => { showToast(message, 'Success'); }}
            onSaveError={(message) => { showToast(message, 'Alert'); }}
            user={user}
            fetchSaveAdditionalContacts={fetchSaveAdditionalContacts}
          />
        );
        setActive(true);
        setChanges(false);
        break;
      case EditActionEnum.SECURITY_QUESTIONS:
        // Note: Security Questions tracking is handled inside the component itself
        setTitle(translationHandler('accountSettings.edit.questions.title'));
        setContent(
          <EditSecurityQuestionsFormView
            authService={authService}
            translationHandler={translationHandler}
            trackingManager={trackingManager}
            localeCode={localeCode}
            questions={securityQuestions}
            userQuestions={userSecurityQuestions}
            onSuccess={() => {
              setActive(false);
              setChanges(false);
              fetchUserSecurityQuestions();
              showToast(translationHandler('accountSettings.edit.questions.success'), 'Success');
            }}
            onChange={() => {
              setChanges(true);
            }}
            isCanada={isCanada}
          />,
        );
        setActive(true);
        setChanges(false);
        break;
      case EditActionEnum.PIN:
        // Note: PIN tracking is handled inside the component itself
        setContent(
          <EditSecurityPinView
            authService={authService}
            translationHandler={translationHandler}
            trackingManager={trackingManager}
            localeCode={localeCode}
            onTitleChange={(t, terms) => {
              setTitle(t);
              setNoTranslateTerms(terms);
            }}
            onSuccess={() => {
              setActive(false);
              setChanges(false);
              showToast(
                applyTextTransformations(
                  translationHandler('accountSettings.edit.pin.success'),
                  ['PIN'],
                ),
                'Success',
              );
            }}
            onChange={() => {
              setChanges(true);
            }}
            isCanada={isCanada}
          />,
        );
        setActive(true);
        setChanges(false);
        break;
      case EditActionEnum.EMAIL_ADDRESS:
        // Note: Email tracking is handled inside the component itself
        setEditEmailModal(true);
        break;
    }
  };

  const handleInitiateDeactivateAccount = () => {
    setTitle(
      translationHandler('accountSettings.deactivate.myvwaccount.title'),
    );
    setNoTranslateTerms(['myVW']);

    if (user?.userRole !== 'no-vehicle' && !user?.vehicles) {
      setContent(
        <DeactivateAccountErrorView
          translationHandler={translationHandler}
          isDesktop={isDesktop}
          fetchAdditionalVehiclesInformation={fetchAdditionalVehiclesInformation}
          onClose={() => { setActive(false); }}
        />,
      );
    } else {
      setContent(
        <DeactivateAccountView
          translationHandler={translationHandler}
          trackingManager={trackingManager}
          isDesktop={isDesktop}
          isCanada={isCanada}
          isFrench={isFrench}
          user={user}
          logout={() => { setActive(false); logout(); }}
          userRole={user?.userRole}
          onGoBack={() => { setActive(false); }}
          handleDeactivateConfirmation={handleOnDeactivateConfirmation}
        />,
      );
    }

    setActive(true);
    setChanges(false);
  };

  const handleOnDeactivateConfirmation = () => {
    setTitle(
      translationHandler(
        'accountSettings.deactivate.myvwaccount.confirmation.title',
      ),
    );
    setContent(
      <DeactivateAccountConfirmationView
        authService={authService}
        translationHandler={translationHandler}
        isDesktop={isDesktop}
        localeCode={localeCode}
        logout={logout}
        user={user ?? null}
        userRole={user?.userRole}
        handleConfirmDeactivation={handleAccountDeactivationLoading}
        handleDeactivateAction={handleInitiateDeactivateAccount}
        setIsDeactivating={setIsDeactivating}
        onSuccess={() => { showToast(translationHandler('accountSettings.deactivate.myvwaccount.success.message'), 'Success'); }}
        onError={() => { showToast(translationHandler('accountSettings.deactivate.myvwaccount.error.message'), 'Alert') }}
        onClose={() => { setActive(false); }}
      />,
    );
  };

  const handleAccountDeactivationLoading = async () => {
    setTitle('');
    setContent(<DeactivateAccountLoadingView translationHandler={translationHandler} />);
  };

  const items = [
    {
      key: 'personal-information',
      labelKey: 'accountSettings.personalInformation',
      content: (
        <>
          <ListItem
            title={translationHandler('accountSettings.edit.fullName.title')}
            description={applyTextTransformations(getFullName(userData), [
              getFullName(userData),
            ])}
            actionLabel={translationHandler('accountSettings.edit')}
            actionFunction={() => {
              // Track link click for Full Name
              trackLinkClick('Full Name');
              handleEditAction(EditActionEnum.FULL_NAME);
            }}
            active={title === 'Full Name' && active}
          />
          <Divider />
          <ListItem
            title={translationHandler('accountSettings.edit.homeAddress.title')}
            description={applyTextTransformations(
              getFormattedAddress(userData),
              [getFormattedAddress(userData)],
            )}
            actionLabel={translationHandler('accountSettings.edit')}
            actionFunction={() => {
              // Track link click for Home Address
              trackLinkClick('Home Address');
              handleEditAction(EditActionEnum.HOME_ADDRESS);
            }}
            active={title === 'Home Address' && active}
          />
          <Divider />
          <ListItem
            title={translationHandler(
              'accountSettings.edit.mobileNumber.title',
            )}
            description={applyTextTransformations(formattedNumber, [
              formattedNumber,
            ])}
            actionLabel={translationHandler('accountSettings.edit')}
            actionFunction={() => {
              // Track link click for Mobile Number
              trackLinkClick('Mobile Number');
              handleEditAction(EditActionEnum.MOBILE_NUMBER);
            }}
            active={title === 'Mobile Number' && active}
          />
        </>
      ),
    },
    {
      key: 'security',
      labelKey: 'accountSettings.security',
      content: myVwFaRolloutAccountSettingsSecurity ? (
        <>
          <ListItem
            title={translationHandler('accountSettings.edit.email.title')}
            description={applyTextTransformations(userData.email, [
              userData.email,
            ])}
            actionLabel={<Arrow />}
            actionFunction={() => {
              // Track link click for Email Address
              trackLinkClick('Email Address');

              handleEditAction(EditActionEnum.EMAIL_ADDRESS);
            }}
          />
          <Divider />
          <ListItem
            title={applyTextTransformations(
              translationHandler('accountSettings.edit.pin.title'),
              ['PIN'],
            )}
            description={applyTextTransformations(
              translationHandler('accountSettings.edit.pin.description'),
              ['PIN'],
            )}
            actionLabel={<Arrow />}
            actionFunction={() => {
              // Track link click for PIN
              trackLinkClick('PIN');
              handleEditAction(EditActionEnum.PIN);
            }}
            active={title === 'Get verification code' && active}
          />
          <Divider />
          <ListItem
            title={translationHandler('accountSettings.edit.questions.title')}
            description={translationHandler(
              'accountSettings.edit.questions.description',
            )}
            actionLabel={<Arrow />}
            actionFunction={() => {
              // Track link click for Security Questions
              trackLinkClick('Security Questions');
              handleEditAction(EditActionEnum.SECURITY_QUESTIONS);
            }}
            active={title === 'Security Questions' && active}
          />
          {!isCanada && (user?.vehicles?.length ?? 0) > 0 ? (
            <>
              <Divider />
              <ListItem
                title={translationHandler('accountSettings.manage.contacts.title')}
                description={translationHandler(
                  'accountSettings.manage.contacts.description',
                )}
                actionLabel={<Arrow />}
                actionFunction={() => {
                  // Track link click for Manage Contacts
                  trackLinkClick('Manage Contacts');
                  handleEditAction(EditActionEnum.MANAGE_CONTACTS);
                }}
              />
              <Divider />
            </>
          ) : <Divider />
          }
          <ListItem
            title={applyTextTransformations(
              translationHandler(
                'accountSettings.deactivate.myvwaccount.title',
              ),
              ['myVW'],
            )}
            description={translationHandler(
              'accountSettings.deactivate.myvwaccount.description',
            )}
            actionLabel={
              <DeactivateAccountCTA
                defaultLabel={translationHandler(
                  'accountSettings.deactivate.myvwaccount.title',
                )}
                mobileLabel={translationHandler(
                  'accountSettings.deactivate.myvwaccount.cta.mobile',
                )}
                disabled={isLoadingVehicleData}
              />
            }
            actionFunction={() => {
              // Track link click for Deactivate Account
              trackLinkClick('Deactivate Account');

              handleInitiateDeactivateAccount();
            }}
            active={title === 'Deactivate myVW account' && active}
          />
        </>
      ) : (
        <FeatureAppLoader
          featureAppId="mmyvw-account-settings-security"
          featureAppName={featureAppName}
          src={src}
          baseUrl={baseUrl}
          serverSrc={serverSrc}
          config={{
            ...config,
            currentScreen: 'security',
          }}
        />
      ),
    }
  ];

  return {
    active,
    changes,
    goBack,
    setChanges,
    content,
    editEmailModal,
    isDeactivating,
    items,
    noTranslateTerms,
    setActive,
    setEditEmailModal,
    setNoTranslateTerms,
    title,
  };
};